import React, { useState, useEffect, useMemo } from 'react';
import Strapi from '../../services/strapi';
import { XGrid, RowsProp, ColDef, CellParams } from '@material-ui/x-grid';
import { Button, makeStyles, Grid } from '@material-ui/core';
import Article from '../../interfaces/article';
import ArticleList from '../../organisms/ArticleList';
import { useNotification } from '../../contexts/Notification';

interface CoverEditorProps {
	cover_name: string,
	collection_name: string,
	article_list_params?: any,
};

const useStyles = makeStyles(() => ({
	list: {
		height: '100vh',
	},
	divider: {
		backgroundColor: 'red',
		height: '10px'
	},
	grids: {
		width: '100%'
	}
}));

const CoverEditor = (props: CoverEditorProps) => {
	const [articlesInCover, setArticleInCover] = useState<Article[]>([]);
	const rows1 = useMemo<RowsProp>(() => {
		return articlesInCover.slice(0, Math.min(7, articlesInCover.length)).map((e, i) => ({
			id: i,
			type: e.type,
			slug: e.data.slug,
			title: e.data.title,
			theme: e.data.theme ? e.data.theme.name : "",
			action: e.data.slug,
		}))
	}, [articlesInCover]);
	const rows2 = useMemo<RowsProp>(() => {
		return (articlesInCover.length >= 7) ? articlesInCover.slice(7, articlesInCover.length).map((e, i) => ({
			id: i + 7,
			type: e.type,
			slug: e.data.slug,
			title: e.data.title,
			theme: e.data.theme ? e.data.theme.name : "",
			action: e.data.slug,
		})) : [];
	}, [articlesInCover])
	const classes = useStyles();
	const notification = useNotification();

	useEffect(() => {
		Strapi.get("cover-" + props.cover_name).then((r) => {
			setArticleInCover(r.body);
		})
	}, [props.cover_name]);

	const save = () => {
		notification.notify("saving...", "info");
		Strapi.put("cover-" + props.cover_name, {},
			props.collection_name === "all" ?
				{
					atlantico_lights: articlesInCover.filter((e) => e.type === 'atlantico-light').map((e) => e.data.id),
					decryptages: articlesInCover.filter((e) => e.type === 'decryptage').map((e) => e.data.id),
					dossiers: articlesInCover.filter((e) => e.type === 'dossier').map((e) => e.data.id),
					pepites: articlesInCover.filter((e) => e.type === 'pepite').map((e) => e.data.id),
					podcasts: articlesInCover.filter((e) => e.type === 'podcast').map((e) => e.data.id),
					rdvs: articlesInCover.filter((e) => e.type === 'rdv').map((e) => e.data.id),
					videos: articlesInCover.filter((e) => e.type === 'video').map((e) => e.data.id),
					order: articlesInCover.map(e => e.data.slug),
				} : {
					body: articlesInCover.map((e) => (e.data.id)),
					order: articlesInCover.map(e => e.data.slug),
				}
		).then(r => {
			notification.notify("cover succefully saved");
		}).catch(e => {
			notification.notify("fail to save cover", "error");
		});
	}

	const addArticleToCover = (article: Article) => {
		let isIn = false;
		for (let x of articlesInCover) {
			if (x.data.slug === article.data.slug) {
				isIn = true;
			}
		}
		if (!isIn) {
			const r = [article, ...articlesInCover];
			setArticleInCover(r.slice(0, Math.min(14, r.length)));
		}
	};

	const columns: ColDef[] = [
		{ field: 'type', headerName: "Type", width: 130, sortable: false },
		{ field: 'title', headerName: 'Titre', width: 300, sortable: false },
		{ field: 'theme', headerName: 'Theme', width: 130, sortable: false },
		{
			field: 'action', headerName: 'Actions', width: 300, renderCell: (params: CellParams) => (
				<>
					<Button onClick={() => {
						setArticleInCover(articlesInCover.filter((e) => {
							return e.data.slug !== params.data.slug
						}))
					}}>Supprimer</Button>
					<Button onClick={() => {
						if (params.data.id > 0) {
							setArticleInCover(old => {
								const new_tab = old.slice();
								const i = params.data.id as number;
								const temp = new_tab[i];
								new_tab[i] = new_tab[i - 1]
								new_tab[i - 1] = temp;
								return new_tab;
							})
						}
					}}>Up</Button>
					<Button onClick={() => {
						if (params.data.id < articlesInCover.length - 1) {
							setArticleInCover(old => {
								const new_tab = old.slice();
								const i = params.data.id as number;
								const temp = new_tab[i];
								new_tab[i] = new_tab[i + 1]
								new_tab[i + 1] = temp;
								return new_tab;
							})
						}
					}}>Down</Button>
				</>
			), sortable: false,
		}
	];

	return (
		<Grid container spacing={2}>
			<Grid item sm={12}>
				<Grid container spacing={2}>
					<Grid item>
						<Button variant="contained" color="primary" onClick={() => {
							setArticleInCover([]);
						}}>
							Réinitialiser
						</Button>
					</Grid>
					<Grid item>
						<Button variant="contained" color="primary" onClick={save}>
							Sauvegarder
						</Button>
					</Grid>
				</Grid>
			</Grid>
			<Grid item sm={12}>
				<div className={classes.grids}>
					<XGrid
						autoHeight
						rows={rows1}
						columns={columns}
						hideFooter
					/>
					<div className={classes.divider}/>
					<XGrid
						headerHeight={0}
						autoHeight
						rows={rows2}
						columns={columns}
						hideFooter
					/>
				</div>
			</Grid>
			<Grid item sm={12} className={classes.list}>
				<ArticleList
					params={props.article_list_params}
					collection_name={props.collection_name} 
					action={(article: Article) => {
						return <Button onClick={() => {
							addArticleToCover(article);
						}}>ADD</Button>
					}} />
			</Grid>
		</Grid>
	);
};

export default CoverEditor;
